import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ALLERGENS, FLAVOURS, INGREDIENTS, MAY_CONTAIN, NUTRITION_FACTS as N, STORAGE } from "@/data/content";

const Row = ({ r }) => (
  <div className={`flex justify-between gap-3 border-t border-[#1B1530]/15 py-1 ${r.indent ? "pl-3" : ""}`}>
    <span><span className={r.bold ? "font-bold" : ""}>{r.label}</span> {r.amount}</span>
    <span className="font-bold">{r.dv}</span>
  </div>
);

const NutritionFacts = () => (
  <div data-testid="nutrition-facts-table" className="text-xs text-[#1B1530]">
    <div className="flex items-end justify-between pb-1">
      <span className="font-bold">{N.serving} · Calories {N.calories}</span>
      <span className="font-semibold">% DV*</span>
    </div>
    <div className="grid sm:grid-cols-2 sm:gap-x-6">
      {[...N.rows, ...N.minerals].map((r) => <Row key={r.label} r={r} />)}
    </div>
    <p className="mt-1.5 text-[#4A4560]">*5% or less is a little, 15% or more is a lot. Same for every flavour.</p>
  </div>
);

const Ingredients = ({ k }) => (
  <div data-testid={`ingredients-${k}`} className="space-y-1.5 text-xs leading-relaxed text-[#1B1530]">
    <p>{INGREDIENTS[k]}</p>
    <p data-testid="ingredients-allergens" className="font-bold">{ALLERGENS}</p>
    <p className="text-[#4A4560]">{MAY_CONTAIN} {STORAGE}</p>
  </div>
);

const Tab = ({ id, tab, setTab, children }) => (
  <button data-testid={`product-info-tab-${id}`} onClick={() => setTab(id)}
    className={`h-8 px-3 rounded-full text-xs font-bold transition-colors ${tab === id ? "bg-[#1B1530] text-white" : "bg-[#1B1530]/5 hover:bg-[#1B1530]/10"}`}>
    {children}
  </button>
);

// Compact "Ingredients & nutrition" dropdown for 6-pack pages (one flavour) and the box builder (pick a flavour)
export const ProductInfo = ({ flavourKeys, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ingredients");
  const [k, setK] = useState(flavourKeys[0]);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={`rounded-2xl bg-white border border-[#1B1530]/10 ${className}`}>
      <CollapsibleTrigger data-testid="product-info-toggle" className="w-full flex items-center justify-between gap-3 px-4 h-12 text-sm font-bold text-left">
        Ingredients & nutrition facts
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </CollapsibleTrigger>
      <CollapsibleContent data-testid="product-info-content" className="px-4 pb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Tab id="ingredients" tab={tab} setTab={setTab}>Ingredients</Tab>
          <Tab id="nutrition" tab={tab} setTab={setTab}>Nutrition facts</Tab>
          {flavourKeys.length > 1 && tab === "ingredients" && (
            <select data-testid="product-info-flavour-select" value={k} onChange={(e) => setK(e.target.value)}
              className="h-8 rounded-full border border-[#1B1530]/20 bg-white px-3 text-xs font-bold">
              {flavourKeys.map((key) => <option key={key} value={key}>{FLAVOURS.find((f) => f.key === key).name}</option>)}
            </select>
          )}
        </div>
        {tab === "ingredients" ? <Ingredients k={k} /> : <NutritionFacts />}
      </CollapsibleContent>
    </Collapsible>
  );
};
